import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage({ userType = 'doctor', portalName = 'Doctor', onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/password-reset/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, user_type: userType })
      });

      if (res.ok) {
        setSent(true);
        toast.success('Password reset email sent');
      } else {
        const data = await res.json();
        // Still show success to prevent email enumeration
        if (res.status === 503) {
          toast.error(data.detail || 'Email service unavailable');
        } else {
          setSent(true);
        }
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getGradient = () => {
    switch (userType) {
      case 'vendor': return 'from-rose-500 to-pink-600';
      case 'admin': return 'from-slate-700 to-slate-900';
      default: return 'from-teal-500 to-emerald-600';
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200">
        <Card className="max-w-md md:max-w-lg w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Check Your Email</h2>
            <p className="text-slate-600 mb-6">
              If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Button variant="outline" onClick={onBack} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200">
      <Card className="max-w-md md:max-w-lg w-full shadow-xl">
        <CardHeader className={`bg-gradient-to-r ${getGradient()} text-white rounded-t-lg`}>
          <CardTitle className="text-xl">Forgot Password</CardTitle>
          <CardDescription className="text-white/80">
            {portalName} Portal - Password Recovery
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your registered email"
                className="mt-1"
                required
                data-testid="forgot-password-email"
              />
            </div>

            <p className="text-sm text-slate-500">
              Enter the email address associated with your account and we'll send you a link to reset your password.
            </p>

            <Button 
              type="submit" 
              className={`w-full bg-gradient-to-r ${getGradient()} hover:opacity-90`}
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" /> Send Reset Link
                </>
              )}
            </Button>

            <Button 
              type="button" 
              variant="ghost" 
              onClick={onBack} 
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
